import { createIdentifier } from '@blocksuite/global/di';
import type { ExtensionType } from '@blocksuite/store';
import type { ReadonlySignal } from '@preact/signals-core';

/** One entry of the canvas dock: an existing quick or senior tool. */
export interface EdgelessDockEntryInfo {
  /** `quick:<extension id>` or `senior:<extension id>`; unique in the dock. */
  readonly id: string;
  readonly kind: 'quick' | 'senior';
  /** The entry's name for tooltips and the AI: the senior tool's name, a quick tool's tool type. */
  readonly name: string;
  /** A quick tool with a menu item can open from the dock's overflow menu. */
  readonly overflows: boolean;
  /** The canvas's current tool is this entry's tool. */
  readonly active: boolean;
  /** The entry's room in the row, px. */
  readonly width: number;
}

export interface EdgelessDockState {
  /** `tools` while editing, `presenting` while frames are presented, `none` on a read-only canvas. */
  readonly mode: 'tools' | 'presenting' | 'none';
  /** The entries in row order; empty until a row is attached. */
  readonly entries: readonly EdgelessDockEntryInfo[];
  /** The canvas locks the dock (e.g. while an element is being dragged). */
  readonly locked: boolean;
  /** While presenting, the dock hides until the pointer comes near. */
  readonly autoHide: boolean;
}

/** The entry id of the presentation controls while frames are presented. */
export const EDGELESS_DOCK_PRESENTATION_ENTRY = 'presentation';

/**
 * A canvas's dock content, rendered by an app-owned dock. The app attaches the row the entries live
 * in and mounts each entry into a container of its own; the entries stay the canvas's Lit elements.
 */
export interface EdgelessDock {
  readonly state$: ReadonlySignal<EdgelessDockState>;
  /**
   * The row the entries live in until the returned release: entry menus open above it, dragged
   * shapes scale inside it, and canvas fits keep content clear of it. It must be positioned.
   */
  attach(row: HTMLElement): () => void;
  /** Renders an entry (or the presentation controls) into `container` until the returned dispose. */
  mount(id: string, container: HTMLElement): () => void;
  /** Opens the menu of the quick tools `ids` above `target`; closes with the returned dispose. */
  openOverflow(target: HTMLElement, ids: readonly string[]): () => void;
  /** The width the dock may take; the presentation controls go dense below their needs. */
  setRoom(width: number): void;
}

/** Where a canvas publishes its dock; without a sink (e.g. a preview editor) the canvas has none. */
export interface EdgelessDockSink {
  publish(dock: EdgelessDock): () => void;
}

export const EdgelessDockSinkIdentifier =
  createIdentifier<EdgelessDockSink>('edgeless-dock-sink');

export function EdgelessDockSinkExtension(
  sink: EdgelessDockSink
): ExtensionType {
  return {
    setup: di => {
      di.addImpl(EdgelessDockSinkIdentifier, () => sink);
    },
  };
}
